<template>
  <div
    v-if="showBanner"
    class="fixed bottom-0 bg-gray-900 text-white p-4 shadow-lg border-t border-gray-700 transition-all duration-300"
    :class="{
      'left-72': !isSidebarCollapsed && isAuthenticated && !isMobile,
      'left-20': isSidebarCollapsed && isAuthenticated && !isMobile,
      'left-0': !isAuthenticated || isMobile
    }"
    style="right: 0; z-index: 60;"
  >
    <div class="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div class="flex-1">
        <h3 class="text-lg font-semibold mb-2">🍪 Utilisation des cookies</h3>
        <p class="text-sm text-gray-300 leading-relaxed">
          Nous utilisons des cookies techniques nécessaires au fonctionnement de l'application 
          (authentification, sécurité). Aucun cookie de tracking ou publicitaire n'est utilisé.
        </p>
      </div>
      <div class="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <button
          @click="acceptCookies"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900"
        >
          Compris
        </button>
        <button
          @click="showDetails"
          class="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900"
        >
          Détails
        </button>
      </div>
    </div>
    <!-- Modal des détails -->
    <div
      v-if="showModal"
      class="fixed inset-0 flex items-center justify-center bg-white/10 backdrop-blur-md"
      style="z-index: 70;"
      @click="closeModal"
    >
      <div
        class="bg-gray-800 text-white rounded-xl p-6 max-w-2xl mx-4 max-h-[80vh] overflow-y-auto shadow-2xl border border-gray-700"
        @click.stop
      >
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold text-white">🍪 Détails sur les cookies</h2>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-white text-3xl font-bold transition-colors duration-200 hover:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center"
          >
            ×
          </button>
        </div>
        <div class="space-y-6">
          <div class="bg-green-900/20 border border-green-500/30 p-4 rounded-lg">
            <h3 class="font-semibold text-green-400 mb-3 flex items-center">
              <span class="text-lg mr-2">✅</span>
              Cookies techniques (nécessaires)
            </h3>
            <ul class="text-sm space-y-2 ml-6 text-gray-200">
              <li class="flex items-start">
                <span class="text-green-400 mr-2">•</span>
                <span><strong class="text-white">Cookies de session</strong> : Maintien de votre connexion</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-400 mr-2">•</span>
                <span><strong class="text-white">Cookies CSRF</strong> : Protection contre les attaques</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-400 mr-2">•</span>
                <span><strong class="text-white">Stockage local</strong> : Sauvegarde de vos préférences d'authentification</span>
              </li>
            </ul>
          </div>
          <div class="bg-red-900/20 border border-red-500/30 p-4 rounded-lg">
            <h3 class="font-semibold text-red-400 mb-3 flex items-center">
              <span class="text-lg mr-2">❌</span>
              Cookies non utilisés
            </h3>
            <ul class="text-sm space-y-2 ml-6 text-gray-300">
              <li class="flex items-start">
                <span class="text-red-400 mr-2">•</span>
                <span>Aucun cookie de tracking</span>
              </li>
              <li class="flex items-start">
                <span class="text-red-400 mr-2">•</span>
                <span>Aucun cookie publicitaire</span>
              </li>
              <li class="flex items-start">
                <span class="text-red-400 mr-2">•</span>
                <span>Aucun cookie d'analyse</span>
              </li>
            </ul>
          </div>
          <div class="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg">
            <h4 class="font-semibold mb-3 text-blue-400 flex items-center">
              <span class="text-lg mr-2">🔒</span>
              Sécurité
            </h4>
            <p class="text-sm text-gray-200 leading-relaxed">
              Tous nos cookies sont sécurisés (flag Secure) et ne sont transmis qu'en HTTPS.
              Les données stockées localement sont chiffrées et ne contiennent aucune information personnelle sensible.
            </p>
          </div>
        </div>
        <div class="flex justify-end mt-8">
          <button
            @click="acceptCookies"
            class="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
          >
            J'ai compris
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted } from 'vue'
withDefaults(defineProps<{
  isSidebarCollapsed?: boolean
  isAuthenticated?: boolean
  isMobile?: boolean
}>(), {
  isSidebarCollapsed: false,
  isAuthenticated: false,
  isMobile: false
})
const showBanner = ref(false)
const showModal = ref(false)
const COOKIE_CONSENT_KEY = 'cookie_consent_accepted'
onMounted(() => {
  // Vérifier si l'utilisateur a déjà donné son consentement
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
  if (!consent) {
    showBanner.value = true
  }
})
const acceptCookies = () => {
  localStorage.setItem(COOKIE_CONSENT_KEY, 'true')
  showBanner.value = false
  showModal.value = false
}
const showDetails = () => {
  showModal.value = true
}
const closeModal = () => {
  showModal.value = false
}
</script>
<style scoped>
/* Animation pour l'apparition du banner */
.fixed {
  animation: slideUp 0.3s ease-out;
}
@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>
